import { useEffect, useState } from 'react'

import { CYCLE_PARAM } from '@/modules/projects/constants'
import type { Statement } from '@/modules/projects/stores'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'
import { useStatements } from '../../hooks'
import { ReconciliationWorkspace } from '../dialogs/ReconciliationWorkspace'

const readCycleParam = () => {
  const value = new URLSearchParams(window.location.search.slice(1)).get(
    CYCLE_PARAM,
  )
  const segmentId = Number(value)
  return Number.isInteger(segmentId) && segmentId > 0 ? segmentId : null
}

const replaceUrlSearch = (searchParams: URLSearchParams) => {
  const query = searchParams.toString()
  // Keep the existing state: the router keys its history entries there, and blanking it makes a
  // later Back read as a fresh navigation.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}`,
  )
}

const writeCycleParam = (segmentId: number | null) => {
  const searchParams = new URLSearchParams(window.location.search.slice(1))
  if (searchParams.get(CYCLE_PARAM) === (segmentId?.toString() ?? null)) return
  if (segmentId == null) searchParams.delete(CYCLE_PARAM)
  else searchParams.set(CYCLE_PARAM, String(segmentId))
  replaceUrlSearch(searchParams)
}

const statementOwning = (statements: Statement[], segmentId: number) =>
  statements.find((statement) =>
    statement.segments?.some((segment) => segment.id === segmentId),
  )

/**
 * Keeps the open reconcile cycle in the page's query string (`?cycle=<segment id>`), so a reload or
 * a shared link lands back on the same billing cycle.
 *
 * Called by the reconciliation view, which owns the workspace dialog. Other routes deliberately do
 * not call it, so none of them write the param.
 *
 * Behavior:
 *   - Once the statements have loaded, opens the workspace on the cycle named in the URL.
 *   - Closes a workspace left open from an earlier visit when the URL names no cycle.
 *   - Mirrors every later cycle change (previous / next, a card's own button) back into the URL.
 *   - Drops the param when the workspace closes or the view unmounts.
 *
 * Writes through `history.replaceState` rather than the router, matching `useFiltersUrlSync`.
 */
export const useCycleUrlSync = () => {
  const statements = useStatements()
  const { openDialog, closeDialog } = useDialogActions()
  const isOpen = useDialogIsOpen(ReconciliationWorkspace.id)
  const target = useDialogData() as { segmentId?: number } | undefined
  const openSegmentId = isOpen ? (target?.segmentId ?? null) : null
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    if (hydrated || statements.length === 0) return
    setHydrated(true)

    const segmentId = readCycleParam()
    const statement =
      segmentId == null ? undefined : statementOwning(statements, segmentId)

    // The dialog store outlives this page, so a cycle left open on an earlier visit would come back
    // over a URL that names no cycle. On arrival the URL is the authority.
    if (segmentId == null || !statement) {
      if (segmentId != null) writeCycleParam(null)
      if (isOpen) closeDialog()
      return
    }

    openDialog(ReconciliationWorkspace.id, {
      statementId: statement.id,
      segmentId,
    })
  }, [hydrated, statements, openDialog, closeDialog, isOpen])

  useEffect(() => {
    if (!hydrated) return
    writeCycleParam(openSegmentId)
  }, [hydrated, openSegmentId])

  useEffect(() => () => writeCycleParam(null), [])
}
